"use client";

import posthog from "posthog-js";
import { useEffect } from "react";
import { useAtom } from "jotai";
import { acceptedCookiePolicyAtom } from "~/atoms";
import { CookieTypeEnum } from "~/types-and-enums/cookieTypes";
import { isProductionEnv } from "~/utils/envConfig";

/**
 * Initialises PostHog once the user has accepted Analytics cookies.
 * Renders nothing — purely a side-effect component.
 *
 * Autocapture and automatic pageviews are disabled so we have full
 * control over what is sent (manual tracking via usePostHog hook).
 */
const PostHogProvider = () => {
  const [acceptedCookiePolicy] = useAtom(acceptedCookiePolicyAtom);

  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;
  const hasAnalyticsConsent = acceptedCookiePolicy.includes(CookieTypeEnum.Analytics);

  useEffect(() => {
    // Only initialise in production — DEV and local environments use console logging only.
    if (!isProductionEnv()) return;

    if (!apiKey || !apiHost || !hasAnalyticsConsent) return;

    // Prevent double-initialisation on hot reloads
    if (posthog.__loaded) return;

    posthog.init(apiKey, {
      api_host: apiHost,

      // ── Event filtering ──────────────────────────────────────────────────
      // Block internal SDK events before they leave the browser so they never
      // count against the free-tier quota.
      //   $web_vitals — browser performance metrics (not needed)
      //   $identify   — internal call fired when posthog.identify() runs
      //   $opt_in     — fired when the user accepts cookies
      //   $set        — internal person-property update calls
      before_send: (event) => {
        if (!event) return null;
        const EXCLUDED = ["$web_vitals", "$identify", "$opt_in", "$set"];
        return EXCLUDED.includes(event.event) ? null : event;
      },

      // ── Event capture ────────────────────────────────────────────────────
      // All events are sent manually via usePostHog — disable auto-captures.
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,

      // ── Remote config ────────────────────────────────────────────────────
      // Skip the /decide endpoint so PostHog cannot remotely activate the
      // toolbar or push feature-flag overrides.  Session recording still works
      // because it is configured locally below.
      advanced_disable_flags: true,

      // ── Session recording ────────────────────────────────────────────────
      // Record sessions for UX analysis.  All form inputs are masked so that
      // passwords, personal data and other sensitive values are never captured.
      session_recording: {
        maskAllInputs: true,
      },

      // ── Unused features ──────────────────────────────────────────────────
      // Explicitly off so PostHog never injects heatmap overlays, survey
      // widgets or A/B-test DOM mutations into the live page.
      enable_heatmaps: false,
      capture_heatmaps: false,
      disable_surveys: true,
      disable_web_experiments: true,

      // ── Persistence ──────────────────────────────────────────────────────
      persistence: "localStorage",
    });
  }, [apiKey, apiHost, hasAnalyticsConsent]);

  // Opt user out if consent is revoked after initialisation
  useEffect(() => {
    if (!posthog.__loaded) return;
    if (!hasAnalyticsConsent) {
      posthog.opt_out_capturing();
    } else {
      posthog.opt_in_capturing();
    }
  }, [hasAnalyticsConsent]);

  return null;
};

export default PostHogProvider;
